import React from "react";
import { useSelector } from "react-redux";
import { hasPermission } from "../../../utils/permissions";

/**
 * Wrapper for admin page components that enforces permission checks.
 * If the user lacks the required permission(s), they see an "Access Denied" page
 * instead of the component.
 *
 * Usage in AppRouter:
 *   <RootRouter
 *     path="/admin/exams"
 *     layout={AdminLayout}
 *     component={withPermission(ListExams, PERMISSIONS.EXAMS_VIEW)}
 *     protectedUrl={true}
 *   />
 *
 * Or with multiple permissions:
 *   component={withPermission(AddExam, [PERMISSIONS.EXAMS_CREATE, PERMISSIONS.EXAMS_VIEW])}
 */
const AccessDenied = () => (
  <div className="content-wrapper">
    <section className="content-header">
      <h1>Access Denied</h1>
    </section>
    <section className="content">
      <div className="row">
        <div className="col-md-8 col-md-offset-2">
          <div
            style={{
              textAlign: "center",
              padding: "60px 20px",
            }}
          >
            <i
              className="fa fa-lock"
              style={{
                fontSize: 64,
                color: "#dd4b39",
                marginBottom: 20,
                display: "block",
              }}
            ></i>
            <h2 style={{ color: "#333", marginBottom: 10 }}>
              You don't have permission to access this page
            </h2>
            <p style={{ color: "#777", fontSize: 15, marginBottom: 30 }}>
              Your current role does not include the required permissions. Please
              contact a Super Admin if you need access.
            </p>
            <a href="/admin" className="btn btn-primary btn-lg">
              <i className="fa fa-home"></i> Go to Dashboard
            </a>
          </div>
        </div>
      </div>
    </section>
  </div>
);

/**
 * HOC that wraps a component with a permission check.
 * @param {React.Component} WrappedComponent - The component to protect
 * @param {string|string[]} requiredPermissions - Permission(s) required
 * @param {string} mode - 'any' (default) or 'all'
 */
export const withPermission = (WrappedComponent, requiredPermissions, mode = "any") => {
  const PermissionWrapper = (props) => {
    const { user } = useSelector((state) => state.auth);

    // While loading, render the component (it will show its own loader)
    if (!user) {
      return <WrappedComponent {...props} />;
    }

    // Check permission
    if (!hasPermission(user, requiredPermissions, mode)) {
      return <AccessDenied />;
    }

    return <WrappedComponent {...props} />;
  };

  PermissionWrapper.displayName = `withPermission(${
    WrappedComponent.displayName || WrappedComponent.name || "Component"
  })`;

  return PermissionWrapper;
};

export default AccessDenied;
